import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faPlay,
  faDownload,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faRocket,
  faUsers,
  faFileAlt,
  faCalendarAlt,
  faBuilding,
  faSearch,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import {
  getApiUrl,
  SERVERS,
  ENDPOINTS,
} from "../config/api";
import SimilarityMatrixDisplay from "../components/SimilarityMatrixDisplay";
import "./ReviewerAssigner.css";

const ReviewerAssigner = () => {
  // Filter state for HOG role
  const [filterData, setFilterData] = useState({
    academicYear: "",
    semester: "",
    groupDepartmentName: "",
  });

  // Available CSV uploads state
  const [availableUploads, setAvailableUploads] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [loadingUploads, setLoadingUploads] = useState(false);

  // Similarity results (raw from backend)
  const [results, setResults] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Assignment state
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentResults, setAssignmentResults] = useState(null);
  const [assignmentError, setAssignmentError] = useState(null);
  const [reviewersPerPaper, setReviewersPerPaper] = useState(2);
  const [maxPapersPerReviewer, setMaxPapersPerReviewer] = useState(3);

  // Drag and drop state
  const [draggedCourse, setDraggedCourse] = useState(null);
  const [draggedFromProfessor, setDraggedFromProfessor] = useState(null);
  const [dropTargetProfessor, setDropTargetProfessor] = useState(null);

  // Academic years dropdown
  const academicYears = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 10; i++) {
    const year = currentYear - i;
    academicYears.push(`${year}-${(year + 1).toString().slice(-2)}`);
  }

  useEffect(() => {
    // Load available uploads when component mounts
    loadAvailableUploads();
  }, []);

  // loadFilteredUploads is un-memoized but reads exactly filterData's
  // academicYear/semester/groupDepartmentName, already listed below as the
  // deps that trigger this effect.
  useEffect(() => {
    // Load filtered uploads when filter changes
    if (filterData.academicYear && filterData.semester && filterData.groupDepartmentName) {
      loadFilteredUploads();
    } else {
      setSelectedUpload(null);
      setResults(null);
      setAssignmentResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData.academicYear, filterData.semester, filterData.groupDepartmentName]);

  const loadAvailableUploads = async () => {
    setLoadingUploads(true);
    try {
      const response = await fetch(
        getApiUrl(SERVERS.DATABASE, "CSV_UPLOADS") // We'll need to add this endpoint
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableUploads(data.uploads || []);
      }
    } catch (e) {
      console.error("Failed to load available uploads:", e);
      setError("Failed to load available CSV uploads");
    } finally {
      setLoadingUploads(false);
    }
  };

  const loadFilteredUploads = async () => {
    setLoadingUploads(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        academic_year: filterData.academicYear,
        semester: filterData.semester,
        group_department_name: filterData.groupDepartmentName,
      });

      const response = await fetch(
        `${getApiUrl(SERVERS.DATABASE, "CSV_UPLOADS")}?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.upload) {
          setSelectedUpload(data.upload);
        } else {
          setSelectedUpload(null);
          setError("No CSV files found for the selected combination. Please ensure the Data Operator has uploaded files for this Academic Year, Semester, and Group/Department.");
        }
      } else {
        setSelectedUpload(null);
        setError("No CSV files found for the selected combination.");
      }
    } catch (e) {
      console.error("Failed to load filtered uploads:", e);
      setError("Failed to load CSV files for the selected criteria");
      setSelectedUpload(null);
    } finally {
      setLoadingUploads(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
    setResults(null);
    setAssignmentResults(null);
    setAssignmentError(null);
  };

  const handleGenerateMatrix = async () => {
    if (!selectedUpload) {
      setError("No CSV files available for the selected criteria");
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setAssignmentResults(null);
    setAssignmentError(null);

    try {
      // Create FormData with the CSV content from the selected upload
      const formData = new FormData();
      
      // Create blob files from the stored CSV data
      const professorsBlob = new Blob([selectedUpload.professors_csv_data], { type: 'text/csv' });
      const coursesBlob = new Blob([selectedUpload.courses_csv_data], { type: 'text/csv' });
      const feedbackBlob = new Blob([selectedUpload.feedback_csv_data], { type: 'text/csv' });

      formData.append("professors_file", professorsBlob, "professors.csv");
      formData.append("courses_file", coursesBlob, "courses.csv");
      formData.append("feedback_file", feedbackBlob, "feedback.csv");

      const csvUrl = getApiUrl(SERVERS.REVIEWER, ENDPOINTS.SIMILARITY_CSV);
      const response = await fetch(csvUrl, { method: "POST", body: formData });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate similarity matrix");
      }
      const data = await response.json();
      setResults(data);
    } catch (e) {
      console.error("Error generating similarity:", e);
      setError(e.message || "Failed to generate similarity");
    } finally {
      setLoading(false);
    }
  };

  // Adapter: if backend returned experience-based similarity (dict), convert to legacy grid for display
  const adaptedForDisplay = useMemo(() => {
    if (!results) return null;

    // Legacy shape already fits the display
    if (
      results.similarity_matrix &&
      results.professors &&
      results.question_papers
    ) {
      return {
        similarityMatrix: results.similarity_matrix,
        professors: results.professors,
        questionPapers: results.question_papers,
        summaryStats: results.summary_stats,
        generatedAt: results.generated_at,
      };
    }

    // Experience shape: { similarity: {prof_id: {course_id: score}}, labels, stats, ... }
    if (results.similarity && results.labels) {
      const profIds = Object.keys(results.similarity);
      // collect all course IDs
      const courseIdSet = new Set();
      profIds.forEach((pid) =>
        Object.keys(results.similarity[pid] || {}).forEach((cid) =>
          courseIdSet.add(cid)
        )
      );
      const courseIds = Array.from(courseIdSet).sort();

      // build matrix
      const matrix = profIds.map((pid) =>
        courseIds.map((cid) => {
          const v =
            (results.similarity[pid] && results.similarity[pid][cid]) ?? 0;
          return typeof v === "number" ? v : parseFloat(v) || 0;
        })
      );

      // build minimal professor meta for the display table
      const profMeta = profIds.map((pid) => {
        const p =
          (results.labels.professors && results.labels.professors[pid]) || {};
        return {
          id: pid,
          name: p.name || pid,
          email: p.email || "",  // Extract email from labels
          department: p.dept || p.department || "",
        };
      });

      // build minimal course meta for the display table using course_id
      const paperMeta = courseIds.map((cid) => {
        const courseInfo = (results.labels.courses && results.labels.courses[cid]) || {};
        return {
          id: cid,  // Use course_id as id
          filename: cid,  // shown as column header in display (course code)
          course_code: cid,  // course_code = course_id
          course_title: courseInfo.name || cid,  // Display name from labels
          department: courseInfo.dept || "",
          topics: [],
          keywords: [],
          difficulty_level: "Easy",
          question_types: ["Mixed"],
          learning_objectives: [],
          prerequisites: [],
          content_text: "",
          duration_hours: 3.0,
          total_marks: 100,
          academic_level: "UG",
          semester: "1",
          year: "2024",
        };
      });

      const summaryStats = results.stats
        ? {
            total_professors: results.stats.total_professors,
            total_papers: results.stats.total_courses,
            matrix_shape: [profIds.length, courseIds.length],
            average_similarity: results.stats.average_similarity ?? 0,
            max_similarity: results.stats.max_similarity ?? 0,
            min_similarity: results.stats.min_similarity ?? 0,
            std_similarity: results.stats.std_similarity ?? 0,
            generated_at: results.generated_at || new Date().toISOString(),
            top_matches: [], // optional
          }
        : {
            total_professors: profIds.length,
            total_papers: courseIds.length,
            matrix_shape: [profIds.length, courseIds.length],
            average_similarity: 0,
            max_similarity: 0,
            min_similarity: 0,
            std_similarity: 0,
            generated_at: results.generated_at || new Date().toISOString(),
            top_matches: [],
          };

      return {
        similarityMatrix: matrix,
        professors: profMeta,
        questionPapers: paperMeta,
        summaryStats,
        generatedAt: summaryStats.generated_at,
      };
    }

    return null;
  }, [results]);

  const handleGenerateAssignment = async () => {
    if (!results) {
      setAssignmentError("Please generate similarity first");
      return;
    }
    setAssignmentLoading(true);
    setAssignmentError(null);
    setAssignmentResults(null);

    try {
      // Legacy fallback (if backend returns the old matrix)
      if (adaptedForDisplay?.similarityMatrix) {
        const cleanMatrix = adaptedForDisplay.similarityMatrix.map((row) =>
          row.map((v) => +v || 0)
        );
        const assignmentRequest = {
          similarity_matrix: cleanMatrix,
          demand: parseInt(reviewersPerPaper) || 2,  // Use reviewers per paper (demand)
          ability: parseInt(maxPapersPerReviewer) || 3,  // Use max papers per reviewer (supply)
          iteration_limit: 10,
          time_limit: 300,
        };
        const url = getApiUrl(SERVERS.REVIEWER, ENDPOINTS.ASSIGNMENT_RUN);
        const resp = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(assignmentRequest),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to generate assignment");
        }
        const assignmentResult = await resp.json();

        // Transform for table using adaptedForDisplay professors/papers
        const enhanced = {
          ...assignmentResult,
          generated_at: new Date().toISOString(),
          professor_assignments: adaptedForDisplay.professors
            .map((prof, profIndex) => {
              const assignedPapers = [];
              Object.entries(assignmentResult.assignment || {}).forEach(
                ([paperIdx, reviewerIds]) => {
                  if (reviewerIds.includes(String(profIndex))) {
                    const p =
                      adaptedForDisplay.questionPapers[parseInt(paperIdx)];
                    if (p) assignedPapers.push(p);
                  }
                }
              );
              return {
                professor_name: prof.name,
                professor_email: prof.email || `${prof.name.toLowerCase().replace(/[^a-z0-9]+/g, '.')}@university.edu`,  // Use email from CSV, fallback if missing
                department: prof.department,
                assigned_papers: assignedPapers,
              };
            })
            .filter((p) => p.assigned_papers.length > 0),
          total_assignments: Object.values(
            assignmentResult.assignment || {}
          ).reduce((s, v) => s + v.length, 0),
          papers_assigned: Object.keys(assignmentResult.assignment || {})
            .length,
          coverage_percentage: Math.round(
            (Object.keys(assignmentResult.assignment || {}).length /
              (adaptedForDisplay.questionPapers?.length || 1)) *
              100
          ),
        };
        setAssignmentResults(enhanced);
        return;
      }

      throw new Error("Unexpected data shape for assignment");
    } catch (e) {
      console.error("Error generating assignment:", e);
      setAssignmentError(e.message || "Failed to generate assignment");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const canGenerate = selectedUpload && !loading;

  // Drag and Drop Handlers
  const handleDragStart = (e, course, professorIndex) => {
    setDraggedCourse(course);
    setDraggedFromProfessor(professorIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, professorIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetProfessor(professorIndex);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDropTargetProfessor(null);
  };

  const handleDrop = (e, targetProfessorIndex) => {
    e.preventDefault();
    
    if (!draggedCourse || draggedFromProfessor === null) return;
    
    // Don't do anything if dropping on the same professor
    if (draggedFromProfessor === targetProfessorIndex) {
      setDraggedCourse(null);
      setDraggedFromProfessor(null);
      setDropTargetProfessor(null);
      return;
    }

    // Create a copy of assignment results
    const updatedAssignments = [...assignmentResults.professor_assignments];
    
    // Remove course from source professor
    const sourceProfessor = updatedAssignments[draggedFromProfessor];
    sourceProfessor.assigned_papers = sourceProfessor.assigned_papers.filter(
      (paper) => paper.id !== draggedCourse.id
    );
    
    // Add course to target professor
    const targetProfessor = updatedAssignments[targetProfessorIndex];
    // Check if course already exists (shouldn't happen, but safety check)
    const courseExists = targetProfessor.assigned_papers.some(
      (paper) => paper.id === draggedCourse.id
    );
    if (!courseExists) {
      targetProfessor.assigned_papers.push(draggedCourse);
    }
    
    // Update the results with new assignments
    setAssignmentResults({
      ...assignmentResults,
      professor_assignments: updatedAssignments,
    });
    
    // Reset drag state
    setDraggedCourse(null);
    setDraggedFromProfessor(null);
    setDropTargetProfessor(null);
  };

  const handleDragEnd = (e) => {
    setDraggedCourse(null);
    setDraggedFromProfessor(null);
    setDropTargetProfessor(null);
  };

  return (
    <div className="reviewer-assigner-page">
      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FontAwesomeIcon icon={faChartLine} className="page-icon" style={{ marginRight: "6px" }}/>
            Reviewer Assigner (HOG)
          </h1>
          <p className="page-description">
            Select uploaded CSV files by Academic Year, Semester, and Group/Department to generate similarity matrix and reviewer assignments.
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        <div className="section-header">
          <h2>
            <FontAwesomeIcon icon={faFilter} className="section-icon" style={{ marginRight: "6px" }}/>
            Step 1: Select CSV Data
          </h2>
          <p>Choose the Academic Year, Semester, and Group/Department to load the corresponding CSV files</p>
        </div>

        <div className="filter-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="academicYear">
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "6px" }}/>
                Academic Year *
              </label>
              <select
                id="academicYear"
                name="academicYear"
                value={filterData.academicYear}
                onChange={handleFilterChange}
                className="form-control"
                required
              >
                <option value="">Select Academic Year</option>
                {academicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="semester">
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "6px" }}/>
                Semester *
              </label>
              <select
                id="semester"
                name="semester"
                value={filterData.semester}
                onChange={handleFilterChange}
                className="form-control"
                required
              >
                <option value="">Select Semester</option>
                <option value="1">Semester 1</option>
                <option value="2">Semester 2</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="groupDepartmentName">
                <FontAwesomeIcon icon={faBuilding} style={{ marginRight: "6px" }}/>
                Group/Department Name *
              </label>
              <select
                id="groupDepartmentName"
                name="groupDepartmentName"
                value={filterData.groupDepartmentName}
                onChange={handleFilterChange}
                className="form-control"
                required
              >
                <option value="">Select Group/Department</option>
                <option value="Core Eng">Core Eng</option>
                <option value="CSIS">CSIS</option>
                <option value="Management">Management</option>
                <option value="EEE">EEE</option>
              </select>
            </div>
          </div>

          {loadingUploads && (
            <div className="loading-indicator">
              <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
              <span>Loading CSV files...</span>
            </div>
          )}

          {selectedUpload && (
            <div className="upload-info-card">
              <h3>
                <FontAwesomeIcon icon={faCheckCircle} className="success-icon" style={{ marginRight: "6px" }}/>
                CSV Files Found
              </h3>
              <div className="upload-details">
                <div className="detail-item">
                  <strong>Academic Year:</strong> {selectedUpload.academic_year}
                </div>
                <div className="detail-item">
                  <strong>Semester:</strong> {selectedUpload.semester}
                </div>
                <div className="detail-item">
                  <strong>Group/Department:</strong> {selectedUpload.group_department_name}
                </div>
                <div className="detail-item">
                  <strong>Uploaded By:</strong> {selectedUpload.uploaded_by}
                </div>
                {/* Last Updated, not Upload Date. A re-upload for this
                    combination overrides the same row and leaves uploaded_at at
                    the first-ever upload, so showing uploaded_at made an
                    overridden combination look days stale even though the CSV
                    content behind it was current - which is exactly how this
                    was reported as "Reviewer Assigner shows the old data".
                    First Uploaded appears only once the two differ, i.e. only
                    when the combination has actually been overridden, so an
                    untouched upload still shows a single date. */}
                <div className="detail-item">
                  <strong>Last Updated:</strong> {new Date(selectedUpload.updated_at).toLocaleString()}
                </div>
                {selectedUpload.updated_at !== selectedUpload.uploaded_at && (
                  <div className="detail-item">
                    <strong>First Uploaded:</strong> {new Date(selectedUpload.uploaded_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Errors set while looking for a CSV upload (no match found, load
          failure) arrive with no selectedUpload - rendering this alongside
          the generation section instead of inside it means those errors are
          not silently dropped just because there is nothing to generate
          from yet. */}
      {error && !selectedUpload && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: "6px" }}/>
          <span>{error}</span>
        </div>
      )}

      {/* Generation Section */}
      {selectedUpload && (
        <div className="generation-section">
          <div className="section-header">
            <h2>
              <FontAwesomeIcon icon={faRocket} className="section-icon" style={{ marginRight: "6px" }}/>
              Step 2: Generate Similarity Matrix
            </h2>
          </div>

          <div className="generate-controls">
            <button
              className={`generate-btn ${canGenerate ? "ready" : "disabled"}`}
              onClick={handleGenerateMatrix}
              disabled={!canGenerate}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
                  Generating Matrix...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlay} style={{ marginRight: "6px" }}/>
                  Generate Similarity Matrix
                </>
              )}
            </button>

            <div className="generation-info">
              <div className="info-item">
                <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: "6px" }}/>
                <span>Using uploaded CSV files for {filterData.academicYear} - {filterData.semester} - {filterData.groupDepartmentName}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: "6px" }}/>
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {/* Results Section */}
      {adaptedForDisplay && (
        <div className="results-section">
          <div className="section-header">
            <h2>
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="section-icon success"
                style={{ marginRight: "6px" }}
              />
              Step 3: Similarity Matrix Results
            </h2>
            <div className="results-info">
              <span>
                Generated:{" "}
                {new Date(
                  adaptedForDisplay.generatedAt || Date.now()
                ).toLocaleString()}
              </span>
            </div>
          </div>

          <SimilarityMatrixDisplay
            similarityMatrix={adaptedForDisplay.similarityMatrix}
            professors={adaptedForDisplay.professors}
            questionPapers={adaptedForDisplay.questionPapers}
            summaryStats={adaptedForDisplay.summaryStats}
          />
        </div>
      )}

      {/* Assignment Section */}
      {adaptedForDisplay && (
        <div className="assignment-section">
          <div className="section-header">
            <h2>
              <FontAwesomeIcon icon={faUsers} className="section-icon" style={{ marginRight: "6px" }}/>
              Step 4: Generate Reviewer Assignment
            </h2>
          </div>

          <div className="assignment-controls">
            <div className="assignment-inputs">
              <div className="input-group">
                <label htmlFor="reviewers-per-paper">
                  <FontAwesomeIcon icon={faUsers} style={{ marginRight: "6px" }}/>
                  Reviewers per Course
                </label>
                <input
                  id="reviewers-per-paper"
                  type="number"
                  min="1"
                  max="10"
                  value={reviewersPerPaper}
                  onChange={(e) =>
                    setReviewersPerPaper(parseInt(e.target.value) || 1)
                  }
                  className="assignment-input"
                />
              </div>
              <div className="input-group">
                <label htmlFor="max-courses-per-reviewer">
                  <FontAwesomeIcon icon={faUsers} style={{ marginRight: "6px" }}/>
                  Max Courses per Reviewer
                </label>
                <input
                  id="max-courses-per-reviewer"
                  type="number"
                  min="1"
                  max="20"
                  value={maxPapersPerReviewer}
                  onChange={(e) =>
                    setMaxPapersPerReviewer(parseInt(e.target.value) || 1)
                  }
                  className="assignment-input"
                />
              </div>
            </div>

            <button
              className={`assignment-btn ${
                !assignmentLoading ? "ready" : "disabled"
              }`}
              onClick={handleGenerateAssignment}
              disabled={assignmentLoading}
            >
              {assignmentLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
                  Generating Assignment...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faRocket} style={{ marginRight: "6px" }}/>
                  Generate Assignment
                </>
              )}
            </button>
          </div>

          {assignmentError && (
            <div className="error-message">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: "6px" }}/>
              <span>{assignmentError}</span>
            </div>
          )}
        </div>
      )}

      {/* Assignment Results Section */}
      {assignmentResults && (
        <div className="assignment-results-section">
          <div className="section-header">
            <h2>
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="section-icon success"
                style={{ marginRight: "6px" }}
              />
              Step 5: Assignment Results
            </h2>
            <div className="results-info">
              <span>
                Generated:{" "}
                {new Date(assignmentResults.generated_at).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="assignment-display">
            <div className="assignment-stats">
              <div className="stat-card">
                <FontAwesomeIcon icon={faUsers} style={{ marginRight: "6px" }}/>
                <div className="stat-content">
                  <span className="stat-number">
                    {assignmentResults.total_assignments || 0}
                  </span>
                  <span className="stat-label">Total Assignments</span>
                </div>
              </div>
              <div className="stat-card">
                <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: "6px" }}/>
                <div className="stat-content">
                  <span className="stat-number">
                    {assignmentResults.papers_assigned || 0}
                  </span>
                  <span className="stat-label">Courses Assigned</span>
                </div>
              </div>
              <div className="stat-card">
                <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "6px" }}/>
                <div className="stat-content">
                  <span className="stat-number">
                    {assignmentResults.coverage_percentage || 0}%
                  </span>
                  <span className="stat-label">Coverage</span>
                </div>
              </div>
            </div>

            <div className="assignment-table-container">
              <div className="drag-drop-info">
                <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: "6px" }}/>
                <span>Drag and drop courses between professors to reassign them</span>
              </div>
              <table className="assignment-table">
                <thead>
                  <tr>
                    <th>Professor</th>
                    <th>Department</th>
                    <th>Assigned Courses (Drag to Reassign)</th>
                    <th>Course Count</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentResults.professor_assignments?.map(
                    (assignment, index) => (
                      <tr 
                        key={index}
                        className={`professor-row ${
                          dropTargetProfessor === index ? 'drop-target' : ''
                        }`}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                      >
                        <td className="professor-name">
                          {assignment.professor_name}
                        </td>
                        <td className="department">{assignment.department}</td>
                        <td className="assigned-courses">
                          {assignment.assigned_papers?.map((paper, idx) => (
                            <span 
                              key={idx} 
                              className={`course-tag draggable ${
                                draggedCourse?.id === paper.id ? 'dragging' : ''
                              }`}
                              draggable="true"
                              onDragStart={(e) => handleDragStart(e, paper, index)}
                              onDragEnd={handleDragEnd}
                              title="Drag to reassign to another professor"
                            >
                              {paper.course_code || paper.course_title}
                            </span>
                          ))}
                          {(!assignment.assigned_papers || assignment.assigned_papers.length === 0) && (
                            <span className="no-courses-placeholder">No courses assigned (Drop here)</span>
                          )}
                        </td>
                        <td className="course-count">
                          {assignment.assigned_papers?.length || 0}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            <div className="assignment-actions">
              <button
                className="download-btn"
                onClick={() => {
                  const csvContent =
                    "Professor,Department,Course Code,Course Title\n" +
                    (assignmentResults.professor_assignments
                      ?.flatMap(
                        (assignment) =>
                          assignment.assigned_papers?.map(
                            (paper) =>
                              `"${assignment.professor_name}","${
                                assignment.department
                              }","${paper.course_code || ""}","${
                                paper.course_title || paper.course_code || ""
                              }"`
                          ) || []
                      )
                      .join("\n") || "");
                  const blob = new Blob([csvContent], { type: "text/csv" });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = "course_reviewer_assignments.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                }}
              >
                <FontAwesomeIcon icon={faDownload} style={{ marginRight: "6px" }}/>
                Download Course Assignments CSV
              </button>
              <button
                className="download-btn"
                onClick={async () => {
                  try {
                    const name = window.prompt(
                      "Enter a name for this mapping (will be saved with metadata):"
                    );
                    if (!name) return;
                    
                    const payload = {
                      name: name.trim(),
                      academic_year: filterData.academicYear,
                      semester: filterData.semester,
                      group_department_name: filterData.groupDepartmentName,
                      professor_assignments: assignmentResults.professor_assignments,
                      summary: {
                        total_assignments: assignmentResults.total_assignments,
                        total_papers: assignmentResults.papers_assigned,
                        total_reviewers: assignmentResults.professor_assignments?.length || 0,
                      },
                    };
                    
                    const saveUrl = getApiUrl(
                      SERVERS.REVIEWER,
                      ENDPOINTS.ASSIGNMENT_SAVE
                    );
                    const resp = await fetch(saveUrl, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(payload),
                    });
                    if (!resp.ok) {
                      const err = await resp.json().catch(() => ({}));
                      throw new Error(err.detail || "Failed to save mapping");
                    }
                    await resp.json();
                    window.alert("Mapping saved successfully with metadata!");
                  } catch (e) {
                    console.error("Save mapping error:", e);
                    window.alert(e.message || "Failed to save mapping");
                  }
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "6px" }}/>
                Save Mapping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewerAssigner;
